import useNotificationApi from './notifications'
import { useUser } from '@/stores/user/index'
import { useAuth } from '@/composables/useAuth'
import { useAPI } from '@/composables/useApi'
import type {
	AccountInfo,
	ActivateTeamMemberPayload,
	BusinessProperties,
	IRequestResponse,
	InviteTeamPayload,
	LocalLgaStateContext,
	OTPPayload,
	OTPVerificationPayload,
	OTPVerificationResponse,
	PermissionsDataResponse,
	TeamMembersResponse,
	UpdateBusinessAccountPayload,
	UpdateBusinessAccountResponse,
	UserUpdatePayload
} from '~/types'
import { usePermissionStore } from '@/stores/permissions'

export default function useAccountApi() {
	const { fetchPermissions } = usePermissionStore()
	const Register = async (payload: BusinessSignUpContext & { callback_url: string; product_id: string }) => {
		const { data } = await useAPI<IRequestResponse<VerificationResponse>>('/auth/sign-up', {
			method: 'POST',
			body: { ...payload, platform: 'web' }
		})
		return data
	}

	const LoginApi = async (payload: LoginParameters) => {
		const { data } = await useAPI<IRequestResponse<AccountInfo>>('/auth/sign-in', {
			method: 'POST',
			body: { ...payload, platform: 'web' }
		})
		if (data.value && data.value?.data) {
			const result = data.value.data
			const { setKyc, confirmAccountVerification, checkCACVerification } = useUser()
			const { getTodoList } = useNotificationApi()
			const { setUser } = useAuth()

			if (result.customer?.meta?.sector && Boolean(result?.email_verified_at)) {
				useNotification().alert({
					message: 'Welcome Back',
					status: 200
				})
			}
			setUser(result)
			confirmAccountVerification()
			setKyc(!result.customer?.meta?.sector && Boolean(result?.email_verified_at))
			checkCACVerification()
			if (result.id) {
				await Promise.all([fetchPermissions(result.id), getTodoList()])
			}
		}
		return data
	}
	const ResetPassword = async (payload: PasswordResetContext) => {
		return await useAPI<IRequestResponse>('/auth/reset-password', {
			method: 'POST',
			body: payload
		})
	}
	const PasswordRecoveryRequest = async (payload: PasswordRecoverContext) => {
		return await useAPI<IRequestResponse<{ user_id?: string }>>('/auth/recover-password', {
			method: 'POST',
			body: payload
		})
	}
	const VerifyBusinessAccount = async (payload: Omit<OTPVerificationPayload, 'otp'>) => {
		const { data } = await useAPI<IRequestResponse<AccountInfo>>('/auth/verify-email', {
			method: 'POST',
			body: payload
		})
		return data.value
	}
	const VerifyOTP = async (payload: OTPPayload) => {
		return await useAPI<IRequestResponse<OTPVerificationResponse>>('/otp/verify', {
			method: 'POST',
			body: payload
		})
	}
	const verifyUser = async (body: { email: string; phone: string; name?: string }) => {
		const { data } = await useAPI<IRequestResponse>('/users/find-existing-users', {
			method: 'POST',
			body
		})
		return data.value
	}
	const ResendOtp = async (payload: { email: string; callback_url: string }) => {
		return await useAPI<IRequestResponse<{ message: string }>>('/otp/resend', {
			method: 'POST',
			body: payload
		})
	}

	async function GetAccountInfo(userId?: string | null) {
		const request = await useAPI<IRequestResponse<AccountInfo>>(`/users/${userId || ''}`)
		if (request.data.value?.data) {
			const { setKyc, checkCACVerification } = useUser()
			const { setUser } = useAuth()
			const result = request.data.value.data
			setUser(result)
			setKyc(!result.customer?.meta?.sector)
			checkCACVerification()
		}
		return request
	}
	const updateAccountInfo = async (userId?: string, method: 'PATCH' | 'GET' = 'GET', payload?: UserUpdatePayload) => {
		const request = await useAPI<IRequestResponse<AccountInfo>>(`/users/${userId || ''}`, {
			method,
			body: payload
		})
		if (request.data.value?.data) {
			const { setKyc } = useUser()
			const { setUser } = useAuth()
			const result = request.data.value.data
			setUser(result)
			setKyc(!result.customer?.meta?.sector)
		}
		return request
	}
	async function UpdateUserBio(user_id: string, payload?: UserUpdatePayload | {}) {
		const { data } = await useAPI<IRequestResponse<UserUpdatePayload>>(`/users/${user_id}`, {
			method: 'patch',
			body: payload
		})
		if (data?.value?.data) {
			return data.value.data
		}
		return null
	}

	async function GetSectors() {
		const { data } = await useAPI<IRequestResponse<string[]>>('/resources/sectors')
		if (data.value?.data) {
			const result: SelectOptionContext = data.value.data.map(item => ({
				value: item.toLowerCase(),
				label: item
			}))
			const { setBusinessSectors } = useUser()
			setBusinessSectors(result)
		}
		return data
	}

	const UpdateBusinessAccount = async (
		payload: Partial<UpdateBusinessAccountPayload>,
		method: 'PATCH' | 'POST' = 'PATCH'
	) => {
		const { data } = await useAPI<IRequestResponse<UpdateBusinessAccountResponse | BusinessProperties>>(
			`/business`,
			{
				method,
				body: payload
			}
		)
		return data.value
	}
	const VerifyBusinessAccountUpdate = async (payload: UpdateBusinessAccountResponse) => {
		const { User } = useUser()
		return await useAPI<IRequestResponse<BusinessProperties>>(`/business/${User?.customer?.id ?? ''}`, {
			method: 'POST',
			body: payload
		})
	}

	const InviteTeamMember = async (method: 'POST' | 'DELETE' = 'POST', payload?: InviteTeamPayload) => {
		return await useAPI<IRequestResponse<InviteTeamPayload>>('/invitations', {
			method,
			body: payload
		})
	}

	const ActivateTeamMember = async (payload: ActivateTeamMemberPayload) => {
		return await useAPI<IRequestResponse<BusinessProperties>>('/invitations/activate', {
			method: 'POST',
			body: payload
		})
	}
	const GetTeamMembers = async <T = TeamMembersResponse[]>(
		method: 'GET' | 'PUT' | 'DELETE' = 'GET',
		payload?: TeamMembersResponse | null,
		memberId?: string,
		query?: object
	) => {
		return await useAPI<IRequestResponse<T>>(`/team/${memberId || ''}`, {
			method,
			body: payload,
			query
		})
	}

	const GetTeamMember = async <T = TeamMembersResponse>(memberId: string) => {
		return await useAPI<IRequestResponse<T>>(`/team/member/${memberId}`)
	}

	const UpdateAccountPassword = async (payload: { business_id: string } & UpdatePasswordPayload) => {
		const { business_id, ...rest } = payload
		return await useAPI<IRequestResponse>(`/users/${business_id}/change-password`, {
			body: rest,
			method: 'PATCH'
		})
	}

	const GetStatesWithLGA = async () => {
		const { data } = await useAPI<IRequestResponse<{ states: LocalLgaStateContext[] }>>('/states-with-lga')
		if (Array.isArray(data.value?.data.states)) {
			const { setLocalLgaState } = useUser()
			const result =
				data.value?.data.states.map(item => ({
					lga: item?.lgas?.map(item => ({
						label: item,
						value: item?.toLowerCase() || ''
					})),
					label: item?.state as string,
					value: (item?.state?.toLowerCase() as string) || ''
				})) ?? []
			setLocalLgaState(result)
		}
		return data
	}

	const confirmPasswordApi = async (payload: { password: string }) => {
		const { data } = await useAPI<IRequestResponse<unknown>>('/auth/confirm-password', {
			method: 'POST',
			body: payload
		})
		return data.value
	}
	const saveUnregisteredBusiness = async (payload: {
		name: string
		phone_number: string
		product: string
		product_category: string
	}) => {
		const { data } = await useAPI<IRequestResponse<{ id: string; name: string }>>(
			API_ENDPOINTS.SAVE_UNREGISTERED_BUSINESS,
			{
				method: 'POST',
				body: payload
			}
		)
		return data.value
	}

	return {
		saveUnregisteredBusiness,
		Register,
		verifyUser,
		LoginApi,
		ResetPassword,
		PasswordRecoveryRequest,
		VerifyBusinessAccount,
		VerifyOTP,
		ResendOtp,
		GetAccountInfo,
		GetSectors,
		UpdateBusinessAccount,
		updateAccountInfo,
		VerifyBusinessAccountUpdate,
		InviteTeamMember,
		ActivateTeamMember,
		GetTeamMembers,
		GetTeamMember,
		UpdateAccountPassword,
		GetStatesWithLGA,
		confirmPasswordApi,
		UpdateUserBio
	}
}
